/* eslint-disable @typescript-eslint/no-floating-promises */
import React, { useState } from 'react';
import { TbCircleCheck, TbClock } from 'react-icons/tb';
import { gql, useQuery, useMutation } from '@apollo/client';

import {
  ApolloError,
  Button,
  Divider,
  Heading,
  HStack,
  Icon,
  LinkDivider,
  LoadingIndicator,
  ScrollPane,
  Spacer,
  Text,
  Tooltip,
  useShowMessage,
  VStack,
} from '../../core-ui';
import { toAddress } from './helpers/toAddress';
import { useShowApolloError } from '../../support/graphql/useShowApolloError';

import { CreateSenderAddressDialog } from './CreateSenderAddressDialog';
import { EditSenderAddressDialog } from './EditSenderAddressDialog';

import {
  GetAddresses,
  GetAddresses_myAccount_senderAddresses as SenderAddress,
} from './__generated__/GetAddresses';
import {
  CheckSenderAddress,
  CheckSenderAddressVariables,
} from './__generated__/CheckSenderAddress';
import {
  ResendVerificationEmail,
  ResendVerificationEmailVariables,
} from './__generated__/ResendVerificationEmail';
import {
  DeleteSenderAddress,
  DeleteSenderAddressVariables,
} from './__generated__/DeleteSenderAddress';
import { useAuthState } from '../AuthState';
import { confirm } from '../App/AppDialog';
import { TableView } from '../../core-ui/TableView';

const GET_ADDRESSES = gql`
  query GetAddresses {
    myAccount {
      id
      email
      name
      subdomain
      senderAddresses {
        id
        uid
        domain
        fullName
        forwardTo {
          address
          isVerified
        }
      }
    }
  }
`;

const CHECK_SENDER_ADDRESS = gql`
  mutation CheckSenderAddress($id: String!) {
    checkSenderAddressVerification(id: $id) {
      id
      uid
      domain
      fullName
      forwardTo {
        address
        isVerified
      }
    }
  }
`;

const RESEND_VERIFICATION_EMAIL = gql`
  mutation ResendVerificationEmail($id: String!) {
    resendVerificationEmail(id: $id) {
      id
      uid
      domain
      fullName
      forwardTo {
        address
        isVerified
      }
    }
  }
`;

const DELETE_SENDER_ADDRESS = gql`
  mutation DeleteSenderAddress($id: String!) {
    deleteSenderAddress(id: $id) {
      result
    }
  }
`;

export function SenderAddressSettings() {
  const showMessage = useShowMessage();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [
    editSenderAddress,
    setEditSenderAddress,
  ] = useState<SenderAddress | null>(null);

  const { error, data, refetch } = useQuery<GetAddresses>(GET_ADDRESSES, {
    fetchPolicy: 'network-only',
  });

  if (error) {
    return <ApolloError error={error} />;
  }

  if (!data) {
    return <LoadingIndicator />;
  }

  const { email, name, subdomain, senderAddresses } = data.myAccount;

  return (
    <VStack px={10} pt={10} flex={1} minH={0} align="stretch" spacing={2}>
      <HStack>
        <Heading as="h2" size="lg">
          {t('Sending Addresses')}
        </Heading>
        <Spacer />
        {senderAddresses.length === 0 ? (
          <Button variant="primary" onClick={() => setShowCreateForm(true)}>
            {t('New Sending Address')}
          </Button>
        ) : null}
      </HStack>
      <Divider borderColor="gray.200" />
      <ScrollPane contentContainerProps={{ pb: 10 }}>
        <TableView
          data={senderAddresses}
          onRowClick={(senderAddress) => setEditSenderAddress(senderAddress)}
          columns={[
            {
              title: t('Sender Name'),
              render: (senderAddress) => senderAddress.fullName,
            },
            {
              title: t('Sender Email'),
              render: (senderAddress) => {
                const { uid, domain } = senderAddress;
                return toAddress(uid, domain);
              },
            },
            {
              title: t('Forward Replies to'),
              render: (senderAddress) => senderAddress.forwardTo.address,
            },
            {
              title: t('Status'),
              thAlign: 'center',
              tdProps: { align: 'center' },
              render: (senderAddress) => {
                const { isVerified } = senderAddress.forwardTo;
                return (
                  <VStack>
                    {isVerified ? (
                      <Tooltip label={t('Verified')} placement="top">
                        <HStack>
                          <Icon
                            as={TbCircleCheck}
                            fontSize={20}
                            color="green.500"
                          />
                        </HStack>
                      </Tooltip>
                    ) : (
                      <Tooltip
                        label={t('Pending Verification')}
                        placement="top"
                      >
                        <HStack>
                          <Icon as={TbClock} fontSize={20} color="yellow.500" />
                        </HStack>
                      </Tooltip>
                    )}
                  </VStack>
                );
              },
            },
            {
              title: t(''),
              render: (senderAddress) => (
                <SenderAddressActions senderAddress={senderAddress} />
              ),
              tdProps: { width: '1%', cursor: 'default' },
              onClick: (event) => event.stopPropagation(),
            },
          ]}
        />
        <CreateSenderAddressDialog
          isOpen={showCreateForm}
          onClose={() => {
            setShowCreateForm(false);
          }}
          initialValues={{ uid: subdomain, fullName: name, forwardTo: email }}
          onSaved={() => {
            refetch();
          }}
        />
        <EditSenderAddressDialog
          isOpen={editSenderAddress != null}
          onClose={() => {
            setEditSenderAddress(null);
          }}
          senderAddress={editSenderAddress}
          onSaved={(newSenderAddress) => {
            if (!newSenderAddress.forwardTo.isVerified) {
              const { address } = newSenderAddress.forwardTo;
              showMessage(
                'success',
                t(
                  'A verification email has been sent to "{address}". Please use the link that email to continue.',
                  { address },
                ),
              );
            }
            refetch();
          }}
        />
      </ScrollPane>
    </VStack>
  );
}

function SenderAddressActions(props: { senderAddress: SenderAddress }) {
  const { senderAddress } = props;
  const { id, forwardTo } = senderAddress;
  const { isVerified } = forwardTo;
  const authState = useAuthState();
  const isSystemAdmin = authState.account.system_admin ?? false;
  const showApolloError = useShowApolloError();
  const [checkVerification, { loading: isLoading1 }] = useMutation<
    CheckSenderAddress,
    CheckSenderAddressVariables
  >(CHECK_SENDER_ADDRESS, {
    onError: showApolloError,
    refetchQueries: [GET_ADDRESSES],
  });
  const [resendVerificationEmail, { loading: isLoading2 }] = useMutation<
    ResendVerificationEmail,
    ResendVerificationEmailVariables
  >(RESEND_VERIFICATION_EMAIL, {
    onError: showApolloError,
    refetchQueries: [GET_ADDRESSES],
  });
  const [deleteSenderAddress, { loading: isLoading3 }] = useMutation<
    DeleteSenderAddress,
    DeleteSenderAddressVariables
  >(DELETE_SENDER_ADDRESS, {
    onError: showApolloError,
    refetchQueries: [GET_ADDRESSES],
  });

  const isLoading = isLoading1 || isLoading2 || isLoading3;

  return isLoading ? (
    <Text>{t('Loading...')}</Text>
  ) : (
    <HStack align="stretch" divider={<LinkDivider />}>
      {!isVerified ? (
        <Button
          variant="link"
          onClick={() => checkVerification({ variables: { id } })}
        >
          {t('Refresh')}
        </Button>
      ) : null}
      {!isVerified ? (
        <Button
          variant="link"
          onClick={() => resendVerificationEmail({ variables: { id } })}
        >
          {t('Resend Email')}
        </Button>
      ) : null}
      {isSystemAdmin ? (
        <Button
          variant="link"
          onClick={async () => {
            const shouldProceed = await confirm(
              t('Delete?'),
              t('Press OK to delete this sender address.'),
            );
            if (!shouldProceed) {
              return;
            }
            await deleteSenderAddress({ variables: { id } });
          }}
        >
          {t('Delete')}
        </Button>
      ) : null}
    </HStack>
  );
}
